/**
 * Просмотр журнала консоли записи бага прямо в карточке: сообщения по
 * времени от начала записи (сверяется с видео той же записи), ошибки и
 * предупреждения выделены, стек исключения — целиком.
 */

import { Terminal } from "lucide-react";
import { useMemo, useState } from "react";

import { Button, Checkbox, cn, Input, Modal } from "@/shared/ui";
import { Download } from "@/shared/ui/motionIcons";

import type { Attachment } from "@taskflow/client-core/tasks";
import { type ConsoleLog, isErrorLevel, parseConsoleLog } from "./consoleLog";
import { useAttachmentText } from "./useAttachmentText";

const LEVEL_LABEL: Record<string, string> = {
  error: "Ошибка",
  warn: "Предупр.",
  info: "Инфо",
  log: "Лог",
  debug: "Отладка",
  verbose: "Отладка",
};

function levelClass(level: string): string {
  if (isErrorLevel(level)) return "bg-danger-soft text-danger";
  if (level === "warn") return "bg-warning-soft text-warning";
  return "bg-sunken text-muted";
}

export function AttachmentConsole({ file, onDownload }: { file: Attachment; onDownload: () => void }) {
  const [viewing, setViewing] = useState(false);
  const { load, fetchOnce } = useAttachmentText(file.id, parseConsoleLog);

  const open = () => {
    setViewing(true);
    void fetchOnce();
  };

  return (
    <>
      <button
        type="button"
        className="flex size-10 shrink-0 items-center justify-center rounded border border-line bg-sunken text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
        aria-label={`Открыть журнал ${file.filename}`}
        title="Открыть журнал консоли"
        onClick={open}
      >
        <Terminal className="size-4" aria-hidden />
      </button>

      <Modal
        open={viewing}
        onClose={() => setViewing(false)}
        title={file.filename}
        width="xl"
        footer={
          <Button size="sm" variant="secondary" icon={Download} onClick={onDownload}>
            Скачать .txt
          </Button>
        }
      >
        {load.kind === "ready" ? (
          <ConsoleTable log={load.value} />
        ) : (
          <p className="py-10 text-center text-[13px] text-faint">
            {load.kind === "failed" ? "Не удалось открыть журнал" : "Загрузка…"}
          </p>
        )}
      </Modal>
    </>
  );
}

export function ConsoleTable({ log }: { log: ConsoleLog }) {
  const [query, setQuery] = useState("");
  const [onlyErrors, setOnlyErrors] = useState(false);

  const errors = log.records.filter((record) => isErrorLevel(record.level)).length;
  const warnings = log.records.filter((record) => record.level === "warn").length;
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return log.records.filter(
      (record) =>
        (!onlyErrors || isErrorLevel(record.level)) &&
        (needle === "" ||
          record.text.toLowerCase().includes(needle) ||
          (record.source ?? "").toLowerCase().includes(needle)),
    );
  }, [log.records, query, onlyErrors]);

  return (
    <div className="flex flex-col gap-3">
      {log.header.length > 0 && (
        <p className="text-xs whitespace-pre-line text-muted">{log.header.join("\n")}</p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-48 flex-1">
          <Input
            type="search"
            aria-label="Поиск по сообщениям"
            placeholder="Поиск по сообщениям"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <Checkbox
          checked={onlyErrors}
          onChange={setOnlyErrors}
          label={`Только ошибки (${String(errors)})`}
        />
        <span className="text-xs text-faint">
          Сообщений: {log.records.length}
          {warnings > 0 && ` · предупреждений: ${String(warnings)}`}
        </span>
      </div>

      <div className="max-h-[60dvh] overflow-auto rounded-md border border-line">
        <table className="w-full min-w-[640px] text-left text-xs">
          <thead className="sticky top-0 bg-sunken text-muted">
            <tr>
              <th className="w-20 px-2 py-1.5 font-semibold">Время</th>
              <th className="w-20 px-2 py-1.5 font-semibold">Уровень</th>
              <th className="px-2 py-1.5 font-semibold">Сообщение</th>
              <th className="w-56 px-2 py-1.5 font-semibold">Источник</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((record, index) => (
              <tr
                key={`${record.time}-${String(index)}`}
                className={cn(
                  "border-t border-line align-top",
                  isErrorLevel(record.level) && "text-danger",
                  record.level === "warn" && "text-warning",
                )}
              >
                <td className="px-2 py-1 tabular-nums">{record.time}</td>
                <td className="px-2 py-1">
                  <span className={cn("rounded-sm px-1.5 py-0.5 text-[11px] font-semibold", levelClass(record.level))}>
                    {LEVEL_LABEL[record.level] ?? record.level}
                  </span>
                </td>
                <td className="px-2 py-1">
                  <pre className="font-mono text-[11px] break-words whitespace-pre-wrap">{record.text}</pre>
                </td>
                <td className="max-w-56 truncate px-2 py-1 text-muted" title={record.source ?? ""}>
                  {record.source ?? ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="py-6 text-center text-xs text-faint">
            {log.records.length === 0 ? "Сообщений в консоли не было" : "Ничего не найдено"}
          </p>
        )}
      </div>
    </div>
  );
}
